/**
 * Role Service - Helper functions dla systemu ról
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { RoleName, UserRole, UserWithRoles, Permission } from '../types/database.types';

// ========================================
// SPRAWDZANIE RÓL
// ========================================

/**
 * Sprawdza czy użytkownik ma konkretną rolę
 */
export async function userHasRole(userId: string, roleName: RoleName): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_has_role', {
      user_uuid: userId,
      role_name: roleName,
    });

    if (error) throw error;
    return data ?? false;
  } catch (error) {
    console.error('Error checking user role:', error);
    return false;
  }
}

/**
 * Sprawdza czy użytkownik ma którąkolwiek z podanych ról
 */
export async function userHasAnyRole(userId: string, roleNames: RoleName[]): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('user_has_any_role', {
      user_uuid: userId,
      role_names: roleNames,
    });

    if (error) throw error;
    return data ?? false;
  } catch (error) {
    console.error('Error checking user roles:', error);
    return false;
  }
}

/**
 * Sprawdza czy użytkownik jest adminem
 */
export async function isAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_admin', {
      user_uuid: userId,
    });

    if (error) throw error;
    return data ?? false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

/**
 * Sprawdza czy użytkownik jest moderatorem (lub adminem)
 */
export async function isModerator(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_moderator', {
      user_uuid: userId,
    });

    if (error) throw error;
    return data ?? false;
  } catch (error) {
    console.error('Error checking moderator status:', error);
    return false;
  }
}

/**
 * Pobiera wszystkie role użytkownika
 */
export async function getUserRoles(userId: string): Promise<UserRole[]> {
  try {
    const { data, error } = await supabase.rpc('get_user_roles', {
      user_uuid: userId,
    });

    if (error) throw error;
    return data ?? [];
  } catch (error) {
    console.error('Error fetching user roles:', error);
    return [];
  }
}

/**
 * Pobiera najwyższy priorytet roli użytkownika
 */
export async function getUserMaxRolePriority(userId: string): Promise<number> {
  try {
    const { data, error } = await supabase.rpc('user_max_role_priority', {
      user_uuid: userId,
    });

    if (error) throw error;
    return data ?? 0;
  } catch (error) {
    console.error('Error fetching user max role priority:', error);
    return 0;
  }
}

// ========================================
// ZARZĄDZANIE ROLAMI (Admin/Moderator)
// ========================================

/**
 * Przypisuje rolę użytkownikowi
 */
export async function assignRole(
  userId: string,
  roleName: RoleName,
  assignedBy: string,
  expiresAt?: Date | null
): Promise<{ success: boolean; error?: string }> {
  try {
    // Pobierz ID roli
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', roleName)
      .single();

    if (roleError) throw roleError;
    if (!roleData) throw new Error('Role not found');

    // Przypisz rolę
    const { error: assignError } = await supabase.from('user_roles').insert({
      user_id: userId,
      role_id: roleData.id,
      assigned_by: assignedBy,
      expires_at: expiresAt?.toISOString() ?? null,
    });

    if (assignError) {
      // Jeśli już istnieje, to nie jest błąd
      if (assignError.code === '23505') {
        return { success: true };
      }
      throw assignError;
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error assigning role:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Usuwa rolę użytkownikowi
 */
export async function removeRole(
  userId: string,
  roleName: RoleName
): Promise<{ success: boolean; error?: string }> {
  try {
    // Pobierz ID roli
    const { data: roleData, error: roleError } = await supabase
      .from('roles')
      .select('id')
      .eq('name', roleName)
      .single();

    if (roleError) throw roleError;
    if (!roleData) throw new Error('Role not found');

    // Usuń przypisanie roli
    const { error: deleteError } = await supabase
      .from('user_roles')
      .delete()
      .eq('user_id', userId)
      .eq('role_id', roleData.id);

    if (deleteError) throw deleteError;

    return { success: true };
  } catch (error: any) {
    console.error('Error removing role:', error);
    return { success: false, error: error.message };
  }
}

/**
 * Pobiera wszystkich użytkowników z ich rolami
 */
export async function getAllUsersWithRoles(): Promise<UserWithRoles[]> {
  try {
    const { data, error } = await supabase.from('users_with_roles').select('*');

    if (error) throw error;
    return data ?? [];
  } catch (error) {
    console.error('Error fetching users with roles:', error);
    return [];
  }
}

/**
 * Pobiera wszystkie dostępne role
 */
export async function getAllRoles() {
  try {
    const { data, error } = await supabase
      .from('roles')
      .select('*')
      .order('priority', { ascending: false });

    if (error) throw error;
    return data ?? [];
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
}

// ========================================
// UPRAWNIENIA (PERMISSIONS)
// ========================================

/**
 * Sprawdza czy użytkownik ma konkretne uprawnienie
 */
export async function userHasPermission(userId: string, permission: Permission): Promise<boolean> {
  try {
    const roles = await getUserRoles(userId);

    // Pobierz szczegóły wszystkich ról użytkownika
    const roleIds = roles.map((r) => r.role_id);
    const { data: roleDetails, error } = await supabase
      .from('roles')
      .select('permissions')
      .in('id', roleIds);

    if (error) throw error;

    // Sprawdź czy którakolwiek rola ma to uprawnienie
    const hasPermission = roleDetails?.some((role) => {
      return role.permissions?.includes(permission);
    });

    return hasPermission ?? false;
  } catch (error) {
    console.error('Error checking permission:', error);
    return false;
  }
}

/**
 * Sprawdza czy użytkownik ma wszystkie z podanych uprawnień
 */
export async function userHasAllPermissions(
  userId: string,
  permissions: Permission[]
): Promise<boolean> {
  try {
    const checks = await Promise.all(
      permissions.map((permission) => userHasPermission(userId, permission))
    );

    return checks.every((result) => result === true);
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
}

/**
 * Sprawdza czy użytkownik ma którekolwiek z podanych uprawnień
 */
export async function userHasAnyPermission(
  userId: string,
  permissions: Permission[]
): Promise<boolean> {
  try {
    const checks = await Promise.all(
      permissions.map((permission) => userHasPermission(userId, permission))
    );

    return checks.some((result) => result === true);
  } catch (error) {
    console.error('Error checking permissions:', error);
    return false;
  }
}

/**
 * Pobiera wszystkie uprawnienia użytkownika
 */
export async function getUserPermissions(userId: string): Promise<Permission[]> {
  try {
    const roles = await getUserRoles(userId);

    // Pobierz szczegóły wszystkich ról użytkownika
    const roleIds = roles.map((r) => r.role_id);
    const { data: roleDetails, error } = await supabase
      .from('roles')
      .select('permissions')
      .in('id', roleIds);

    if (error) throw error;

    // Zbierz wszystkie unikalne uprawnienia
    const allPermissions = new Set<Permission>();
    roleDetails?.forEach((role) => {
      role.permissions?.forEach((permission: Permission) => {
        allPermissions.add(permission);
      });
    });

    return Array.from(allPermissions);
  } catch (error) {
    console.error('Error fetching user permissions:', error);
    return [];
  }
}

// ========================================
// MAPOWANIE RÓL NA ETYKIETY I KOLORY
// ========================================

export const ROLE_LABELS: Record<RoleName, string> = {
  user: 'Użytkownik',
  moderator: 'Moderator',
  admin: 'Administrator',
};

export const ROLE_DESCRIPTIONS: Record<RoleName, string> = {
  user: 'Standardowy użytkownik platformy',
  moderator: 'Moderator forum i treści',
  admin: 'Administrator systemu',
};

export const ROLE_COLORS: Record<RoleName, { bg: string; text: string; border: string }> = {
  user: {
    bg: 'var(--dark-3)',
    text: 'var(--text-white-80)',
    border: 'var(--dark-a6)',
  },
  moderator: {
    bg: 'rgba(74, 159, 92, 0.1)',
    text: 'rgba(74, 159, 92, 1)',
    border: 'rgba(74, 159, 92, 0.3)',
  },
  admin: {
    bg: 'rgba(233, 61, 130, 0.1)',
    text: 'var(--pink-9)',
    border: 'rgba(233, 61, 130, 0.3)',
  },
};

/**
 * Helper do renderowania badge'a roli
 */
export function getRoleBadgeStyle(roleName: RoleName) {
  const colors = ROLE_COLORS[roleName];
  return {
    backgroundColor: colors.bg,
    color: colors.text,
    border: `1px solid ${colors.border}`,
    padding: '0.25rem 0.75rem',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
  };
}

